import React, { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  UserGroupIcon,
  UserIcon,
  CurrencyDollarIcon,
  ArrowRightOnRectangleIcon,
  DocumentTextIcon,
  XMarkIcon,
  Bars3Icon,
  CogIcon,
  AcademicCapIcon,
  CalendarIcon,
  BellIcon,
  ChevronDownIcon,
  CommandLineIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '../store/authStore'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from './LanguageSwitcher'
import i18n from '../i18n/config'

interface LayoutProps {
  children?: React.ReactNode
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const menuItems = [
    {
      id: 'calendar',
      name: t('navigation.calendar'),
      href: '/calendar',
      icon: CalendarIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50/50',
      borderColor: 'border-primary-100'
    },
    {
      id: 'materials',
      name: t('navigation.materials'),
      href: '/materials',
      icon: DocumentTextIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50/50',
      borderColor: 'border-primary-100'
    },
    {
      id: 'software-tools',
      name: (i18n.language || 'en').toLowerCase().startsWith('zh') ? '软件工具' : 'Software Tools',
      href: '/software-tools',
      icon: CommandLineIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50/50',
      borderColor: 'border-primary-100'
    },
    {
      id: 'earnings',
      name: t('navigation.earnings'),
      href: '/earnings',
      icon: CurrencyDollarIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50/50',
      borderColor: 'border-primary-100'
    },
    {
      id: 'profile',
      name: t('navigation.profile'),
      href: '/profile',
      icon: UserIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50/50',
      borderColor: 'border-primary-100'
    },
  ]

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('登出失败:', error)
    }
  }

  return (
    <div className="h-screen bg-[#F8FAFC] flex overflow-hidden font-sans relative">
      {/* 背景装饰色彩 */}
      <div className="fixed top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-primary-100/30 rounded-full blur-3xl pointer-events-none z-0"></div>
      <div className="fixed bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 bg-indigo-100/30 rounded-full blur-3xl pointer-events-none z-0"></div>

      {/* 侧边栏 */}
      <div className={`
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 fixed lg:relative z-50 inset-y-0 left-0 w-72 bg-white border-r border-gray-100 lg:flex flex-col transition-all duration-300 ease-in-out shadow-xl lg:shadow-none
      `}>
        {/* Logo区域 */}
        <div className="flex items-center h-20 px-8 border-b border-gray-50 bg-white">
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-10 h-10 bg-gradient-to-tr from-primary-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-indigo-200 shadow-lg transform group-hover:rotate-6 transition-transform duration-300">
              <AcademicCapIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight group-hover:text-primary-600 transition-colors">TalkNect</h1>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                Teacher Portal
              </p>
            </div>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden ml-auto p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 导航菜单 */}
        <nav className="flex-1 px-4 py-8 overflow-y-auto space-y-8">
          <div>
            <div className="space-y-1.5">
              {menuItems.map((item) => {
                const Icon = item.icon
                const active = isActive(item.href)
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      group relative flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300
                      ${active
                        ? `${item.bgColor} ${item.color} shadow-sm border ${item.borderColor}`
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 border border-transparent'
                      }
                    `}
                  >
                    <Icon className={`mr-3.5 h-5 w-5 flex-shrink-0 transition-colors duration-300 ${active ? item.color : 'text-gray-400 group-hover:text-gray-600'}`} />
                    <span>{item.name}</span>
                    {active && (
                      <div className="absolute right-2 w-1.5 h-1.5 rounded-full bg-current opacity-50"></div>
                    )}
                  </Link>
                )
              })}
            </div>
          </div>
        </nav>

        {/* 用户信息区域 */}
        <div className="p-4 border-t border-gray-50 bg-gray-50/50">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md border-2 border-white">
                  <span className="text-white font-bold text-sm">
                    {user?.full_name?.charAt(0)?.toUpperCase() || 'T'}
                  </span>
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {user?.full_name || 'Teacher User'}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-2 text-xs font-bold text-gray-600 bg-gray-50 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-300 group"
            >
              <ArrowRightOnRectangleIcon className="w-3.5 h-3.5 mr-2 group-hover:translate-x-0.5 transition-transform" />
              {t('navigation.logout')}
            </button>
          </div>
        </div>
      </div>

      {/* 移动端遮罩层 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/20 backdrop-blur-sm lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* 主内容区域 */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* 顶部导航栏 */}
        <header className="bg-white border-b border-gray-100 flex-shrink-0 z-30">
          <div className="px-6 h-16 flex justify-between items-center">
            {/* 左侧：移动端菜单按钮 */}
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-xl text-gray-500 hover:text-primary-600 hover:bg-primary-50 transition-all focus:outline-none"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>

              {/* Breadcrumb or Context - Optional */}
              <div className="hidden lg:flex items-center space-x-2 text-sm text-gray-400 font-medium ml-2">
                <span>TalkNect</span>
                <span>/</span>
                <span className="text-gray-900 capitalize">
                  {location.pathname.split('/')[1] || 'Dashboard'}
                </span>
              </div>
            </div>

            {/* 右侧：通知和用户信息 */}
            <div className="flex items-center space-x-3">
              <LanguageSwitcher />

              <div className="h-6 w-px bg-gray-100 mx-2"></div>

              <button className="relative p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all group">
                <BellIcon className="w-5 h-5" />
                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 border-2 border-white rounded-full group-hover:scale-110 transition-transform"></span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2.5 p-1.5 rounded-xl hover:bg-gray-50 transition-all border border-transparent hover:border-gray-100"
                >
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border border-gray-200">
                    {user?.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-gray-500 font-bold text-xs">{user?.full_name?.charAt(0)}</span>
                    )}
                  </div>
                  <ChevronDownIcon className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-300 ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* 下拉菜单 */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="px-4 py-3 border-b border-gray-50">
                      <p className="text-sm font-bold text-gray-900 truncate">{user?.full_name}</p>
                      <p className="text-[10px] text-gray-500 truncate">{user?.email}</p>
                    </div>
                    <div className="p-1.5">
                      <button
                        onClick={() => {
                          navigate('/profile')
                          setUserMenuOpen(false)
                        }}
                        className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-primary-50 hover:text-primary-600 rounded-xl transition-colors"
                      >
                        <UserIcon className="w-4 h-4 mr-2.5 opacity-60" />
                        {t('navigation.profile')}
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2.5 opacity-60" />
                        {t('navigation.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* 页面内容 */}
        <main className="flex-1 overflow-y-auto bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children || <Outlet />}
          </div>
        </main>
      </div>
    </div>
  )
}
